import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR } from './lib';
import { AlternativePicker, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock } from './SharedBlocks';

const StripeLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;
  const cust = quote.customerData || {};
  const card = { background: theme.surface, borderRadius: theme.radius, boxShadow: '0 7px 14px rgba(50,50,93,.07), 0 3px 6px rgba(0,0,0,.05)', padding: '20px 22px', border: `1px solid ${theme.surfaceBorder}` };
  const grad = `linear-gradient(110deg, ${theme.primary} 0%, #3ecfcf 55%, #ff8a65 100%)`;
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: theme.radius, background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.3)', color: '#fff', cursor: 'pointer', fontSize: 12 };

  const Row = ({ service, suffix }) => {
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '14px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.5 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              {isOpt && <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 16, height: 16, accentColor: theme.primary, cursor: 'pointer' }} />}
              <p style={{ fontSize: 15, fontWeight: 600, color: theme.textPrimary, margin: 0 }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 5, lineHeight: 1.6 }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <span style={{ fontSize: 15, fontWeight: 600, color: theme.textPrimary, whiteSpace: 'nowrap' }}>{formatEUR(lineTotal(service))}{suffix ? <span style={{ fontSize: 11, color: theme.textMuted }}> {suffix}</span> : null}</span>
        </div>
      </div>
    );
  };
  const Group = ({ title, services, suffix }) => services.length ? (
    <div style={card}>
      <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: .5, textTransform: 'uppercase', color: theme.primary, margin: '0 0 4px' }}>{title}</p>
      {services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} />)}
    </div>
  ) : null;
  const PriceCard = ({ label, value, suffix, hi }) => (
    <div style={{ flex: 1, background: '#f6f9fc', borderRadius: theme.radius, padding: '12px', textAlign: 'center', border: hi ? `2px solid ${theme.primary}` : '1px solid #e6ebf1' }}>
      <p style={{ fontSize: 11, color: theme.textSecondary, margin: 0 }}>{label}</p>
      <p style={{ fontSize: 20, fontWeight: 600, color: hi ? theme.primary : theme.textPrimary, margin: '4px 0 0' }}>{formatEUR(value)}</p>
      {suffix && <p style={{ fontSize: 10, color: theme.textMuted, margin: '2px 0 0' }}>{suffix}</p>}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 64px' }}>
      {/* Gradient hero band */}
      <div style={{ background: grad, padding: '18px 0 90px', transform: 'skewY(-3deg)', transformOrigin: 'top left', marginBottom: -60 }}>
        <div style={{ transform: 'skewY(3deg)', maxWidth: 880, margin: '0 auto', padding: '0 22px' }}>
          <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 24 }}>
            <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
            <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
          </div>
          {company?.logo && <img src={`${process.env.REACT_APP_BACKEND_URL}${company.logo}`} alt="Logo" style={{ height: 30, marginBottom: 18, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />}
          {cust.company && <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: 'rgba(255,255,255,.85)', marginBottom: 8 }}>Angebot für {cust.company}</p>}
          <h1 style={{ fontSize: 32, lineHeight: 1.15, fontWeight: 700, margin: 0, color: '#fff', maxWidth: 600, letterSpacing: '-0.5px' }}>
            {quote.heroHeadline || quote.quoteTitle || 'Ihr digitales Fundament'}
          </h1>
        </div>
      </div>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: '0 22px', display: 'flex', flexDirection: 'column', gap: 16, position: 'relative' }}>
        <div style={card}>
          {quote.quoteIntro && <p style={{ fontSize: 15, color: theme.textSecondary, lineHeight: 1.7, margin: '0 0 14px', whiteSpace: 'pre-wrap' }}>{quote.quoteIntro}</p>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {einmaligTotals.brutto > 0 && <PriceCard label="Einmalig" value={einmaligTotals.brutto} suffix="inkl. MwSt." />}
            {monatlichTotals.brutto > 0 && <PriceCard label="Monatlich" value={monatlichTotals.brutto} suffix="pro Monat" hi />}
            {jaehrlichTotals.brutto > 0 && <PriceCard label="Jährlich" value={jaehrlichTotals.brutto} suffix="pro Jahr" />}
          </div>
        </div>

        {quote.customerArguments && (
          <div style={{ ...card, borderLeft: `4px solid ${theme.primary}` }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: theme.primary, marginTop: 0, marginBottom: 10 }}>Warum dieses Angebot für Sie?</h3>
            <div style={{ fontSize: 14, color: theme.textSecondary, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
          </div>
        )}
        {quote.videoUrl && <div className="no-print" style={card}><video src={quote.videoUrl} controls style={{ width: '100%', borderRadius: theme.radius }} /></div>}

        <Group title="Einmalige Leistungen" services={einmaligServices} suffix="" />
        <Group title="Monatliche Leistungen" services={monatlichServices} suffix="/ Monat" />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} suffix="/ Jahr" />

        <TermsBlock quote={quote} company={company} theme={theme} />
        <AcceptanceBlock ctx={ctx} theme={theme} />
        <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FeedbackBlock viewHash={viewHash} theme={theme} />
          <QuestionBlock viewHash={viewHash} theme={theme} />
        </div>
        <FooterBlock company={company} theme={theme} />
      </div>
    </div>
  );
};

export default StripeLayout;
